/**
 * Extract the paragraph's EXISTING tracked revisions into a `RevisionContext`, so the engine
 * preserves them instead of re-diffing counterparty edits into new our-side edits (PR review #1).
 *
 * The Word-facing half uses `getTrackedChanges()` (read-only). The pure half — `sanitizeRevisionSpans`
 * — validates/sorts the spans and is unit-tested.
 *
 * ## Robust against the "does range.text include struck text?" question
 * A revision span is kept ONLY if its text actually appears at its computed offset in the live
 * baseline (`baselineVisible.substr(start, len) === revisionText`). So:
 *  - a deletion whose struck text IS in `range.text` is kept → the interleave re-inserts it as a keep →
 *    it survives as a `keep` (stays struck), never re-flagged;
 *  - a deletion whose struck text is NOT in `range.text` is dropped → it isn't in the diff baseline
 *    anyway, so nothing needs doing.
 * This means we never feed the transform a span that isn't genuinely in the baseline, whichever way
 * Word behaves.
 */

import { RevisionContext, RevisionSpan } from './types';
import { logQuickResponseStage } from '../../utils/quickResponseDebug';
import { pickBestParagraphXmlFromPackage } from './applyVerify';

/* global Word */

/** Authors our submit/apply paths use; anything else is the counterparty ("their side"). */
const OUR_SIDE_AUTHORS = new Set([
  'user',
  'user edit',
  'bank of changes',
  'past negotiations',
  'schedule',
  'ai suggestion',
  'quick response',
]);

interface RawRevision {
  kind: 'insertion' | 'deletion';
  start: number;
  end: number;
  side: 'ours' | 'theirs';
  /** The revision's own text, for self-validation against the baseline. */
  text: string;
}

/**
 * Validate and normalize raw revisions into a `RevisionContext`. Pure. Keeps a span only if its text
 * matches the baseline at its offset; sorts by start; drops overlaps (defensive — Word reports
 * revisions in document order, but nested/duplicate reports are possible).
 */
export function sanitizeRevisionSpans(raw: RawRevision[], baselineVisible: string): RevisionContext {
  const valid = raw
    .filter((r) => r.start >= 0 && r.end <= baselineVisible.length && r.end > r.start)
    // self-validate: the span's text must genuinely be at that offset in the diff baseline
    .filter((r) => baselineVisible.slice(r.start, r.end) === r.text)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const revisions: RevisionSpan[] = [];
  let prevEnd = 0;
  for (const r of valid) {
    if (r.start < prevEnd) continue; // drop overlaps
    revisions.push({ kind: r.kind, start: r.start, end: r.end, side: r.side });
    prevEnd = r.end;
  }
  return { revisions };
}

/**
 * Read the paragraph's existing tracked revisions (read-only). Must be called inside a `Word.run`.
 * Returns an empty context on any failure (Win32 can reject `getTrackedChanges`).
 */
export async function extractRevisions(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  baselineVisible: string,
): Promise<RevisionContext> {
  try {
    const wholeStart = paragraph.getRange('Start');
    const tracked = (paragraph as unknown as {
      getTrackedChanges: () => {
        items: Array<{
          load: (p: string[]) => void;
          type?: string;
          author?: string;
          getRange: () => Word.Range;
        }>;
      };
    }).getTrackedChanges();
    (tracked as unknown as { load: (p: string) => void }).load('items');
    await context.sync();

    const items = tracked.items || [];
    logQuickResponseStage('extractRevisions: getTrackedChanges', {
      itemsCount: items.length,
      baselineLen: baselineVisible.length,
    });
    const measured: Array<{ item: (typeof items)[number]; before: Word.Range; range: Word.Range }> = [];
    for (const item of items) {
      item.load(['type', 'author']);
      const range = item.getRange();
      const before = wholeStart.expandTo(range.getRange('Start'));
      before.load('text');
      range.load('text');
      measured.push({ item, before, range });
    }
    await context.sync();

    const raw: RawRevision[] = [];
    for (const m of measured) {
      const type = String(m.item.type ?? '').toLowerCase();
      const kind: 'insertion' | 'deletion' | null =
        type === 'added' ? 'insertion' : type === 'deleted' ? 'deletion' : null;
      if (!kind) continue; // ignore formatting/none
      const text = m.range.text || '';
      if (text === '') continue;
      const start = (m.before.text || '').length;
      const author = String(m.item.author ?? '').trim().toLowerCase();
      raw.push({
        kind,
        start,
        end: start + text.length,
        side: OUR_SIDE_AUTHORS.has(author) ? 'ours' : 'theirs',
        text,
      });
    }

    const sanitized = sanitizeRevisionSpans(raw, baselineVisible);
    logQuickResponseStage('extractRevisions: sanitized', {
      rawCount: raw.length,
      keptCount: sanitized.revisions.length,
      dropped: raw.length - sanitized.revisions.length,
    });
    return sanitized;
  } catch (err) {
    logQuickResponseStage('extractRevisions: FAILED', {
      message: err instanceof Error ? err.message : String(err),
    });
    return { revisions: [] };
  }
}

/**
 * Align revision spans extracted against Word's raw paragraph text to the normalized baseline
 * used for diffing (e.g. after stripping trailing `\r`). Without this, sanitize would drop
 * counterparty revisions whose offsets were measured on the raw string.
 */
export function alignRevisionContextToBaseline(
  revisionContext: RevisionContext,
  baselineVisible: string,
): RevisionContext {
  if (revisionContext.revisions.length === 0) return revisionContext;
  const raw: RawRevision[] = [];
  for (const r of revisionContext.revisions) {
    const end = Math.min(r.end, baselineVisible.length);
    if (r.start >= end || r.start < 0) continue;
    const text = baselineVisible.slice(r.start, end);
    if (!text) continue;
    raw.push({
      kind: r.kind,
      start: r.start,
      end,
      side: r.side ?? 'theirs',
      text,
    });
  }
  return sanitizeRevisionSpans(raw, baselineVisible);
}

/**
 * Mac Word's paragraph.getTrackedChanges() often returns 0 items. Recover
 * insertion/deletion spans from the paragraph's OOXML (All Markup order).
 */
export function revisionsFromParagraphXml(pXml: string, baselineVisible: string): RevisionContext {
  if (!pXml?.trim() || !baselineVisible) return { revisions: [] };
  try {
    const doc = new DOMParser().parseFromString(pXml, 'text/xml');
    if (doc.querySelector('parsererror')) return { revisions: [] };
    const raw: RawRevision[] = [];
    let offset = 0;
    const walk = (node: Node): void => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node as Element;
      const local = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
      if (local === 'del') {
        const start = offset;
        for (const child of Array.from(el.childNodes)) walk(child);
        if (offset > start) {
          raw.push({
            kind: 'deletion',
            start,
            end: offset,
            side: 'theirs',
            text: baselineVisible.slice(start, offset),
          });
        }
        return;
      }
      if (local === 'ins') {
        const start = offset;
        for (const child of Array.from(el.childNodes)) walk(child);
        if (offset > start) {
          raw.push({
            kind: 'insertion',
            start,
            end: offset,
            side: 'ours',
            text: baselineVisible.slice(start, offset),
          });
        }
        return;
      }
      if (local === 't' || local === 'deltext') {
        offset += (el.textContent || '').length;
        return;
      }
      for (const child of Array.from(el.childNodes)) walk(child);
    };
    walk(doc.documentElement);
    return sanitizeRevisionSpans(raw, baselineVisible);
  } catch {
    return { revisions: [] };
  }
}

/** Extract revisions from Word using raw baseline coords, aligned to normalized diff baseline. */
export async function extractRevisionsForNormalizedBaseline(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  rawBaseline: string,
  normalizedBaseline: string,
  ooxmlPackage?: string,
): Promise<RevisionContext> {
  let rawContext = await extractRevisions(context, paragraph, rawBaseline);
  if (rawContext.revisions.length === 0 && ooxmlPackage) {
    const pXml = pickBestParagraphXmlFromPackage(ooxmlPackage, normalizedBaseline || rawBaseline);
    const fromXml = revisionsFromParagraphXml(pXml, rawBaseline);
    if (fromXml.revisions.length > 0) {
      logQuickResponseStage('extractRevisions: recovered from OOXML', {
        revisionCount: fromXml.revisions.length,
        deletions: fromXml.revisions.filter((r) => r.kind === 'deletion').length,
        insertions: fromXml.revisions.filter((r) => r.kind === 'insertion').length,
      });
      rawContext = fromXml;
    }
  }
  if (rawBaseline === normalizedBaseline) return rawContext;
  return alignRevisionContextToBaseline(rawContext, normalizedBaseline);
}
